// Global
import React from 'react';

// Local
import { KitchenTip, RecipeResponse } from 'lib/services/models/content-response';

import { RecipeContentTips } from '../RecipeContent/RecipeContentTips';
import NutritionFacts from 'components/helpers/NutritionFacts';
import { RecipeIngredients } from './RecipeIngredients/RecipeIngredients';
import { ActionToolbar } from 'components/helpers/ActionToolbar';
import styles from './RecipeIngredientInstructionsSection.module.scss';
import { Field } from '@sitecore-content-sdk/nextjs';
import { RecipeInstructions } from './RecipeInstructions/RecipeInstructions';
import { Data } from '@generated/Foundation.GMI.model';
import { getActionToolbarButtons } from 'components/helpers/ActionToolbar/Utils';
import { SiteSettings } from 'lib/page-props-factory/plugins/site-settings';

export interface RecipeIngredientInstructionsSectionProps {
  recipe?: RecipeResponse;
  recipeTips?: KitchenTip[];
  siteSettings?: SiteSettings | null;
}

export function RecipeIngredientInstructionsSection(
  props: RecipeIngredientInstructionsSectionProps
) {
  const recipe = props.recipe;

  if (!recipe) {
    return <div>No recipe found</div>;
  }

  const siteSettings = props.siteSettings;

  const recipeDetailSettings =
    siteSettings?.recipeDetailSiteSettings as Data.Settings.RecipeDetailSiteSettings;

  const hideIngredientsColumn = (
    recipeDetailSettings?.fields?.hideIngredientsComponent as Field<boolean>
  )?.value;

  const hideRdpHeader = recipeDetailSettings?.fields?.hideRdpHeader?.value;

  const ingredientsActionToolbarIdentifier =
    recipeDetailSettings?.fields?.ingredientsEventPosition?.value;

  const actionItems = getActionToolbarButtons(
    recipeDetailSettings?.fields?.ingredientsContentActionToolBar as
      | Data.Content.ActionToolBarButton[]
      | undefined
  );

  return (
    <div
      className={styles.recipeIngredientInstructionsSectionWrapper}
      // only add region attributes if the RDP header is hidden - avoids duplicate regions
      {...(hideRdpHeader && {
        id: 'gmi-recipe-ingredients-instructions-section',
        'aria-label': recipe.Title || '',
        role: 'region',
        tabIndex: -1,
      })}
    >
      <div className={styles.recipeIngredientInstructionsSection}>
        <div
          className={`${styles.recipeCoreGrid} ${hideIngredientsColumn ? styles['recipeCoreGrid--noIngredients'] : ''}`}
        >
          <div className={`${styles.recipeCoreGridActions} printHide`}>
            <ActionToolbar
              className={styles.ingredientsInstructionActionToolbar}
              toolbarIdentifier={ingredientsActionToolbarIdentifier}
              actions={actionItems}
              variant="vertical"
            />
          </div>

          {!hideIngredientsColumn && (
            <div className={styles.recipeCoreGridIngredients}>
              <IngredientsColumn {...props} />
            </div>
          )}

          <div className={styles.recipeCoreGridInstructions}>
            <InstructionsColumn {...props} />
          </div>

          <div className={styles.recipeCoreGridNutrition}>
            <NutritionFacts contentTypeData={recipe} contentType="recipe" />
          </div>
        </div>
      </div>
    </div>
  );
}

function IngredientsColumn(props: RecipeIngredientInstructionsSectionProps) {
  const { recipe } = props;

  if (!recipe) {
    return <></>;
  }

  return (
    <div className={styles.recipeCoreContentLeftColumn}>
      <RecipeIngredients recipe={recipe} />
    </div>
  );
}
// Set to false to hide the NAF table, true to show it
function InstructionsColumn(props: RecipeIngredientInstructionsSectionProps) {
  const { recipe, recipeTips } = props;
  const siteSettings = props.siteSettings;
  if (!recipe) {
    return <></>;
  }

  return (
    <div className={styles.recipeCoreContentRightColumn}>
      <RecipeInstructions recipe={recipe} />
      <RecipeContentTips recipeTips={recipeTips} siteSettings={siteSettings} />
    </div>
  );
}
